import { ArrowDownward } from "@mui/icons-material";
import { Box, Button, Collapse, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useContractSectionsFilter } from "../../hooks/useContractSectionsFilter";
import { dailyViewStaffTableSortingKey, localStorageService } from "../../services/LocalStorageService";
import { INK } from "../../theme/tokens";
import { type BaseNurseShift, type DayItem, NotificationType, type Nurse, type NurseFullRole, type OpenShiftRequest } from "../../utils/models";
import type { SortingState } from "../../utils/multiColumnSorting";
import { StaffTable } from "../StaffTable";
import { defaultSortStateStaffTable, type NurseInShiftWithWage, staffTableColumns } from "../StaffTable/StaffTable";
import { type SortingButtonHandle, SystemSortingButtonWithPopup } from "../UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { DAY_VIEW_CONTENT_PADDING } from "./consts";
import { DayViewFilters } from "./DayViewFilters";
import { DayViewTimeline } from "./DayViewTimeline";
import { useShiftFilters } from "./useShiftFilters";

type DayViewContentProps = {
	date: DateTime;
	weekIndex: number;
	nurses: BaseNurseShift[];
	selectedNurse: Nurse | null;
	selectedLocations: string[];
	setSelectedLocations: Dispatch<SetStateAction<string[]>>;
	availableLocations: string[];
};

export const DayViewContent: FC<DayViewContentProps> = ({
	date,
	weekIndex,
	nurses,
	selectedNurse,
	selectedLocations,
	setSelectedLocations,
	availableLocations,
}) => {
	const { weekViewData, recentCallInsCounts } = useWeekViewContext();
	const { settings } = useUserSettingsContext();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;

	const dayItem = useMemo<DayItem>(() => weekViewData[date.weekdayISO], [weekViewData, date]);

	const { selectedContractSections, handleContractSectionsChange } = useContractSectionsFilter({
		getStored: localStorageService.getDayViewStandardContractSections,
		setStored: localStorageService.setDayViewStandardContractSections,
		analyticsEvent: "dayView_filter_contract",
	});

	const {
		selectedRoles,
		selectedJobs,
		selectedTimes,
		isExpanded,
		setIsExpanded,
		sliderStart,
		setSliderStart,
		sliderEnd,
		setSliderEnd,
		staff,
		shiftTimeFilters,
		onRoleTypeFilterChange,
		onJobTypeFilterChange,
		onShiftTimeFilterChange,
		onShiftClick,
	} = useShiftFilters({
		dayItem,
		date,
		nurses,
		selectedNurse,
		selectedLocations,
		availableLocations,
		showActual: false,
		shiftsForTimeFilters: nurses,
		selectedContractSections,
	});

	const openShiftRequests = useMemo<OpenShiftRequest[]>(() => {
		if (!dayItem) return [];

		return dayItem.requests.filter((request): request is OpenShiftRequest => {
			if (request.request_type !== NotificationType.OpenShift) return false;
			const openShiftRequest = request as OpenShiftRequest;
			const isNeddedNursesRemaining = openShiftRequest.needed_nurses - openShiftRequest.message.accepted_nurses.length > 0;
			return isNeddedNursesRemaining;
		});
	}, [dayItem]);

	const requests = useMemo<OpenShiftRequest[]>(() => {
		return openShiftRequests.filter((request) => {
			const roleMatches = selectedRoles === "All" || request.message.roles.some((role) => selectedRoles.includes(role as NurseFullRole));
			if (!roleMatches) {
				return false;
			}
			if (selectedTimes.length === 0) {
				return true;
			}

			const requestOverlapsSelectedTime = selectedTimes.some((selectedTime) => {
				if (typeof selectedTime === "string") {
					return selectedTime === "All";
				}

				const [timeStart, timeEnd] = selectedTime;

				if (sliderStart === dayItem.earliestTime && sliderEnd === dayItem.latestTime) {
					return true;
				}
				if (settings.overlapShifts) {
					return request.message.shift_start < timeEnd && request.message.shift_end > timeStart;
				}

				return request.message.shift_start.hasSame(timeStart, "hour") && request.message.shift_end.hasSame(timeEnd, "hour");
			});

			return requestOverlapsSelectedTime;
		});
	}, [openShiftRequests, selectedRoles, selectedTimes, settings, dayItem, sliderStart, sliderEnd]);

	const sortingButtonRef = useRef<SortingButtonHandle>(null);
	const storedSortingState = useMemo(() => localStorageService.getTableSortingState<NurseInShiftWithWage>(dailyViewStaffTableSortingKey, userId), [userId]);
	const [sortState, setSortState] = useState<SortingState<NurseInShiftWithWage>[]>(() => {
		return storedSortingState && storedSortingState.length > 0 ? storedSortingState : defaultSortStateStaffTable;
	});
	const handleOnSortHandledFromInside = useCallback(() => {
		sortingButtonRef.current?.openMenu();
	}, []);

	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100%",
				display: "flex",
				flexDirection: "column",
				p: DAY_VIEW_CONTENT_PADDING,
				gap: 1,
			}}
		>
			<Box sx={{ flexShrink: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
					<SystemSortingButtonWithPopup
						ref={sortingButtonRef}
						sortingOptions={staffTableColumns}
						localStorageTableKey={dailyViewStaffTableSortingKey}
						setSortState={setSortState}
						initialState={sortState}
						defaultSortState={defaultSortStateStaffTable}
					/>
					<DayViewFilters
						onRoleTypeFilterChange={onRoleTypeFilterChange}
						onJobTypeFilterChange={onJobTypeFilterChange}
						onShiftTimeFilterChange={onShiftTimeFilterChange}
						shiftsTimeFilters={shiftTimeFilters}
						selectedShiftTimes={selectedTimes}
						selectedRoleTypes={selectedRoles}
						selectedJobs={selectedJobs}
						selectedLocations={selectedLocations}
						setSelectedLocations={setSelectedLocations}
						availableLocations={availableLocations}
						selectedContractSections={selectedContractSections}
						onContractSectionsChange={handleContractSectionsChange}
					/>
					<Box
						component={Button}
						sx={{ display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
						onClick={() => setIsExpanded((expanded) => !expanded)}
					>
						<Typography sx={{ fontWeight: 600, fontSize: { xs: 12, xl: 14 }, color: INK.SECONDARY }}>
							{isExpanded ? "Hide Timeline" : "Show Timeline"}
						</Typography>
						<ArrowDownward
							sx={{
								color: INK.MUTED,
								transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
								transition: "transform 0.3s ease",
								fontSize: { xs: 16, xl: 20 },
							}}
						/>
					</Box>
				</Box>
				<Collapse in={isExpanded}>
					<DayViewTimeline
						date={date}
						nurses={staff}
						requests={requests}
						shiftStart={dayItem.earliestTime.set({ minute: 0 })}
						shiftEnd={dayItem.latestTime.set({ minute: 0 })}
						startTime={sliderStart.set({ minute: 0 })}
						setStartTime={setSliderStart}
						endTime={sliderEnd.set({ minute: 0 })}
						setEndTime={setSliderEnd}
						onShiftClick={onShiftClick}
						selectedRoles={selectedRoles === "All" ? [] : selectedRoles}
						selectedNurse={selectedNurse}
					/>
				</Collapse>
			</Box>
			<Box sx={{ flex: 1, overflow: "auto" }}>
				<StaffTable
					date={date}
					showActual={false}
					staff={staff}
					weekIndex={weekIndex}
					dayIndex={date.weekday % 7}
					requests={requests}
					sortState={sortState}
					setSortState={setSortState}
					onSortHandledFromInside={handleOnSortHandledFromInside}
					recentCallInsCounts={recentCallInsCounts}
					selectedNurse={selectedNurse}
					selectedTimeRange={selectedTimes.some((t) => t === "All") ? undefined : { start: sliderStart, end: sliderEnd }}
					selectedRoles={selectedRoles}
				/>
			</Box>
		</Box>
	);
};
